import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { rf } from "./theme";
import { useCohorts, useFaqs } from "./api/hooks";
import { byRelevance, isEnrollable, statusOf } from "./cohortStatus";
import { useReinforceSeo } from "./seo";
import CohortDialog from "./CohortDialog";
import SiteHeader from "./sections/SiteHeader";
import Hero from "./sections/Hero";
import Cohorts from "./sections/Cohorts";
import Syllabus from "./sections/Syllabus";
import Rhythm from "./sections/Rhythm";
import Honest from "./sections/Honest";
import Instructors from "./sections/Instructors";
import FAQ from "./sections/FAQ";
import FinalCTA from "./sections/FinalCTA";
import SiteFooter from "./sections/SiteFooter";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Reinforce = () => {
  const cohortsQuery = useCohorts();
  const faqsQuery = useFaqs();

  const cohorts = useMemo(() => [...(cohortsQuery.data || [])].sort(byRelevance), [cohortsQuery.data]);
  const leadCohort = cohorts.find(isEnrollable) || cohorts[0] || null;

  // Only cohorts you could still join are worth putting a syllabus tab on.
  const syllabusCohorts = useMemo(() => cohorts.filter((c) => (c.syllabus || []).length > 0), [cohorts]);
  const [syllabusSlug, setSyllabusSlug] = useState(null);
  const activeSyllabusSlug = syllabusSlug || syllabusCohorts[0]?.slug || null;

  const instructors = useMemo(() => {
    const seen = new Map();
    cohorts.forEach((c) => (c.instructors || []).forEach((p) => seen.set(p.id, p)));
    return [...seen.values()];
  }, [cohorts]);

  const [dialog, setDialog] = useState({ open: false, mode: "enroll", cohort: null });

  useReinforceSeo(cohorts);

  // The app theme paints the body grey; this brand sits on warm paper. The
  // display face is loaded here rather than in index.html so no other page pays
  // for it.
  useEffect(() => {
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = rf.cream;

    const href = "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800;900&display=swap";
    let link = document.head.querySelector(`link[href="${href}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }

    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, []);

  const openFor = (cohort) => {
    if (!cohort) {
      scrollTo("cohorts");
      return;
    }
    setDialog({ open: true, mode: statusOf(cohort).action, cohort });
  };

  // "Ask before you commit" is not a checkout: no cohort, no price, just a name
  // and a number so someone can ring back.
  const openAsk = () => setDialog({ open: true, mode: "ask", cohort: leadCohort });

  const showSyllabus = (cohort) => {
    setSyllabusSlug(cohort.slug);
    scrollTo("curriculum");
  };

  return (
    <Box
      sx={{
        backgroundColor: rf.cream,
        backgroundImage: rf.grid,
        backgroundSize: rf.gridSize,
        color: rf.ink,
        fontFamily: rf.body,
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <SiteHeader onPrimary={() => scrollTo("cohorts")} />

      <Box component="main">
        <Hero
          leadCohort={leadCohort}
          loading={cohortsQuery.isLoading}
          onSeeCohorts={() => scrollTo("cohorts")}
          onTalk={openAsk}
        />
        <Cohorts
          cohorts={cohorts}
          loading={cohortsQuery.isLoading}
          error={cohortsQuery.isError}
          onRetry={() => cohortsQuery.refetch()}
          onAction={openFor}
          onSyllabus={showSyllabus}
        />
        <Syllabus
          cohorts={syllabusCohorts}
          selectedSlug={activeSyllabusSlug}
          onSelect={setSyllabusSlug}
          loading={cohortsQuery.isLoading}
        />
        <Rhythm />
        <Honest />
        <Instructors instructors={instructors} />
        <FAQ faqs={faqsQuery.data || []} loading={faqsQuery.isLoading} />
        <FinalCTA leadCohort={leadCohort} onAction={openFor} onTalk={openAsk} />
      </Box>

      <SiteFooter />

      <CohortDialog
        open={dialog.open}
        mode={dialog.mode}
        cohort={dialog.cohort}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
      />
    </Box>
  );
};

export default Reinforce;
